import React, { useEffect, useLayoutEffect, useState, useRef, useCallback } from 'react';
import { useAdoption } from '../../context/AdoptionContext';
import '../../styles/Adoption.css';

function WalkthroughOverlay() {
  const { activeWalkthrough, currentStep, nextStep, prevStep, skipWalkthrough } = useAdoption();
  const [targetRect, setTargetRect] = useState(null);
  // selectorMiss = true → target_selector exists but no element matched after
  // the retry budget. Show a banner instead of silently centering the popover.
  const [selectorMiss, setSelectorMiss] = useState(false);
  const popoverRef = useRef(null);
  // Debounce + rAF handles to keep scroll/resize listeners from causing
  // jitter (testers flagged "shaking/tearing during page changes").
  const rafRef = useRef(null);
  const debounceRef = useRef(null);

  const steps = activeWalkthrough?.steps || [];
  const step = steps[currentStep];

  // Compute popover position clamped to viewport. Responsive height — never
  // exceed 70vh, never exceed actual content height. Popover's own internal
  // scroll handles longer step content (testers flagged "doesn't fit").
  const getPopoverStyle = useCallback((tRect) => {
    const popoverW = Math.min(340, window.innerWidth - 32);
    const margin = 16;
    const maxH = Math.floor(window.innerHeight * 0.7);

    if (!tRect) {
      return { position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', maxWidth: popoverW, maxHeight: maxH };
    }

    const padding = 8;
    const vpW = window.innerWidth;
    const vpH = window.innerHeight;

    // Use actual popover height if available, otherwise estimate
    const popoverH = popoverRef.current
      ? Math.min(popoverRef.current.getBoundingClientRect().height, maxH)
      : 220;

    // Try below target first
    let top = tRect.top + tRect.height + padding + 12;
    let left = Math.max(margin, Math.min(tRect.left, vpW - popoverW - margin));

    // If below goes off viewport, try above
    if (top + popoverH > vpH - margin) {
      top = Math.max(margin, tRect.top - padding - 12 - popoverH);
    }

    // Final clamp
    top = Math.max(margin, Math.min(top, vpH - popoverH - margin));
    left = Math.max(margin, Math.min(left, vpW - popoverW - margin));

    return { position: 'fixed', top, left, maxWidth: popoverW, maxHeight: maxH };
  }, []);

  // Find and highlight target element. The previous implementation re-ran
  // `findTarget` on every resize/scroll event, causing the spotlight to
  // jitter mid-animation. Now we read the rect on a single rAF tick and
  // debounce resize/scroll handlers to that same tick.
  useEffect(() => {
    if (!step?.target_selector) {
      setTargetRect(null);
      setSelectorMiss(false);
      return;
    }
    setSelectorMiss(false);

    let retryCount = 0;
    let cancelled = false;

    const measure = (el) => {
      if (cancelled) return;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => {
        if (cancelled) return;
        const rect = el.getBoundingClientRect();
        setTargetRect({
          top: rect.top,
          left: rect.left,
          width: rect.width,
          height: rect.height,
        });
      });
    };

    const findTarget = () => {
      if (cancelled) return;
      const el = document.querySelector(step.target_selector);
      if (el) {
        // Use 'auto' (instant) for initial location so the popover doesn't
        // chase a moving target during smooth-scroll. Once the element is
        // measured, the user's natural scroll can take over.
        el.scrollIntoView({ behavior: 'auto', block: 'center' });
        measure(el);
      } else if (retryCount < 10) {
        retryCount++;
        setTimeout(findTarget, 500);
      } else {
        // Don't clear targetRect — leaving the prior spotlight visible during
        // a slow page transition feels less broken than the overlay vanishing.
        // Show a banner instead so the user knows the section is unreachable.
        setSelectorMiss(true);
      }
    };

    const debouncedReposition = () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        const el = document.querySelector(step.target_selector);
        if (el) measure(el);
      }, 80);
    };

    findTarget();
    window.addEventListener('resize', debouncedReposition);
    window.addEventListener('scroll', debouncedReposition, true);
    return () => {
      cancelled = true;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      window.removeEventListener('resize', debouncedReposition);
      window.removeEventListener('scroll', debouncedReposition, true);
    };
  }, [step]);

  // After render, recompute position using actual popover height to prevent clipping
  const [, setPositioned] = useState(false);
  useLayoutEffect(() => {
    // Trigger a re-render so getPopoverStyle uses the real popoverRef height
    setPositioned((prev) => !prev);
  }, [currentStep, targetRect]);

  if (!activeWalkthrough || !step) return null;

  const isFirst = currentStep === 0;
  const isLast = currentStep === steps.length - 1;
  const padding = 8;
  const popoverStyle = getPopoverStyle(targetRect);

  return (
    <div className="wt-overlay" onClick={(e) => { if (e.target === e.currentTarget) skipWalkthrough(); }}>
      {/* Dark backdrop with spotlight cutout */}
      <svg className="wt-backdrop" style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', pointerEvents: 'none' }}>
        <defs>
          <mask id="wt-spotlight-mask">
            <rect width="100%" height="100%" fill="white" />
            {targetRect && (
              <rect
                x={targetRect.left - padding}
                y={targetRect.top - padding}
                width={targetRect.width + padding * 2}
                height={targetRect.height + padding * 2}
                rx="8"
                fill="black"
              />
            )}
          </mask>
        </defs>
        <rect
          width="100%"
          height="100%"
          fill="rgba(0,0,0,0.55)"
          mask="url(#wt-spotlight-mask)"
        />
      </svg>

      {/* Spotlight border glow */}
      {targetRect && (
        <div
          className="wt-spotlight-ring"
          style={{
            position: 'fixed',
            top: targetRect.top - padding,
            left: targetRect.left - padding,
            width: targetRect.width + padding * 2,
            height: targetRect.height + padding * 2,
          }}
        />
      )}

      {/* Step popover. Body has overflow:auto so long content scrolls
          inside the card instead of forcing the user to scroll the page. */}
      <div className="wt-popover" style={{ ...popoverStyle, display: 'flex', flexDirection: 'column' }} ref={popoverRef}>
        <div className="wt-popover-header">
          <span className="wt-step-counter">
            Step {currentStep + 1} of {steps.length}
          </span>
          <button className="wt-close-btn" onClick={skipWalkthrough} title="Close walkthrough">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
        <div className="wt-popover-body" style={{ overflowY: 'auto', flex: '1 1 auto', minHeight: 0 }}>
          <h3 className="wt-step-title">{step.title}</h3>
          {selectorMiss && (
            <div
              className="wt-selector-miss"
              style={{
                marginBottom: 10,
                padding: '8px 10px',
                background: '#fef3c7',
                border: '1px solid #fbbf24',
                borderRadius: 6,
                fontSize: 12,
                color: '#92400e',
                lineHeight: 1.4,
              }}
            >
              This section isn't on-screen right now — the description still applies, you can use the navigation to find it, or continue to the next step.
            </div>
          )}
          <p className="wt-step-content">{step.content}</p>
        </div>
        <div className="wt-popover-footer">
          <div className="wt-progress-dots">
            {steps.map((_, i) => (
              <span key={i} className={`wt-dot ${i === currentStep ? 'active' : i < currentStep ? 'done' : ''}`} />
            ))}
          </div>
          <div className="wt-nav-btns">
            {!isFirst && <button className="wt-back-btn" onClick={prevStep}>Back</button>}
            <button className="wt-next-btn" onClick={nextStep}>
              {isLast ? 'Finish' : 'Next'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default WalkthroughOverlay;
